import React from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Checkbox } from "../ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Edit2, UsersRound, Shield, UserCheck, Bell, Mail, CheckCircle2, XCircle, X, Save } from "lucide-react";

/**
 * EditUserModal — Diálogo de edición de usuario (datos, roles, responsable, notificaciones, estado).
 *
 * Extraído 1:1 desde `pages/UsersPage.js`. La lógica (guardado, toggles de rol) permanece
 * en la página; este componente es presentacional y recibe estado/handlers por props.
 *
 * @param {boolean}  props.editModalOpen     Apertura del diálogo.
 * @param {Function} props.setEditModalOpen   Setter del flag de apertura.
 * @param {Object|null} props.selectedUser    Usuario en edición.
 * @param {Function} props.setSelectedUser    Setter del usuario seleccionado (reset al cancelar).
 * @param {Object}   props.editData           Estado del formulario de edición.
 * @param {Function} props.setEditData        Setter del formulario.
 * @param {string[]} props.availableRoles     Roles disponibles para asignar.
 * @param {Function} props.handleRoleToggle   (role) => void — alterna un rol.
 * @param {Function} props.getRoleColor       (role) => string — clases de color del badge.
 * @param {Function} props.getRoleLabel       (role) => string — etiqueta del rol.
 * @param {Array}    props.managers           Usuarios que pueden ser responsables (para COMERCIAL).
 * @param {Function} props.handleSaveUser     Guarda los cambios del usuario.
 */
export const EditUserModal = ({
  editModalOpen,
  setEditModalOpen,
  selectedUser,
  setSelectedUser,
  editData,
  setEditData,
  availableRoles,
  handleRoleToggle,
  getRoleColor,
  getRoleLabel,
  managers,
  handleSaveUser,
}) => {
  return (
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-[#03BEE4]" />
              Editar Usuario
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6 mt-4">
              <div className="space-y-4">
                <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                  <UsersRound className="h-4 w-4" />
                  Información Básica
                </h3>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <Label className="text-slate-300">Nombre *</Label>
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="mt-1 bg-[#0A0A35] border-[#1E1E5F] text-white"
                      data-testid="edit-user-name"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Email</Label>
                    <Input
                      value={editData.email}
                      disabled
                      className="mt-1 bg-[#1B1B4B] border-[#1E1E5F] text-slate-400"
                    />
                    <p className="text-xs text-slate-500 mt-1">El email no se puede modificar</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
                <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Roles de Usuario
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {availableRoles.map((role) => (
                    <div
                      key={role}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        editData.roles?.includes(role)
                          ? "border-[#42E695] bg-[#42E695]/10"
                          : "border-[#1E1E5F] bg-[#1B1B4B]/50 hover:border-[#1E1E5F]/80"
                      }`}
                      onClick={() => handleRoleToggle(role)}
                    >
                      <Checkbox
                        checked={editData.roles?.includes(role)}
                        onCheckedChange={() => handleRoleToggle(role)}
                        className="border-[#1E1E5F] data-[state=checked]:bg-[#42E695] data-[state=checked]:border-[#42E695]"
                      />
                      <div>
                        <Badge className={`${getRoleColor(role)} text-white border-none text-xs`}>
                          {getRoleLabel(role)}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {editData.roles?.includes("COMERCIAL") && (
                <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
                  <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Responsable
                  </h3>
                  <p className="text-xs text-slate-400">El responsable podrá ver y gestionar las oportunidades y clientes de este comercial.</p>
                  <Select
                    value={editData.responsable_id || "__none__"}
                    onValueChange={(v) => {
                      if (v === "__none__") {
                        setEditData({ ...editData, responsable_id: null, responsable_nombre: null });
                      } else {
                        const mgr = managers.find(m => m.id === v);
                        setEditData({ ...editData, responsable_id: v, responsable_nombre: mgr?.name || "" });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white" data-testid="edit-responsable-select">
                      <SelectValue placeholder="Sin responsable" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="__none__" className="text-slate-400">Sin responsable</SelectItem>
                      {managers.filter(m => m.id !== selectedUser?.id).map(m => (
                        <SelectItem key={m.id} value={m.id} className="text-white">{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
                <h3 className="text-[#03BEE4] font-medium flex items-center gap-2">
                  <Bell className="h-4 w-4" />
                  Preferencias de Notificación
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                    <div className="flex items-center gap-3">
                      <Mail className="h-5 w-5 text-[#03BEE4]" />
                      <div>
                        <p className="text-white text-sm font-medium">Notificaciones por Email</p>
                        <p className="text-slate-400 text-xs">Recibir alertas por correo electrónico</p>
                      </div>
                    </div>
                    <Switch
                      checked={editData.notification_email}
                      onCheckedChange={(checked) => setEditData({ ...editData, notification_email: checked })}
                      data-testid="notification-email-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                    <div className="flex items-center gap-3">
                      <Bell className="h-5 w-5 text-[#42E695]" />
                      <div>
                        <p className="text-white text-sm font-medium">Notificaciones en App</p>
                        <p className="text-slate-400 text-xs">Recibir notificaciones en la aplicación</p>
                      </div>
                    </div>
                    <Switch
                      checked={editData.notification_inapp}
                      onCheckedChange={(checked) => setEditData({ ...editData, notification_inapp: checked })}
                      data-testid="notification-inapp-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="h-5 w-5 text-purple-400" />
                      <div>
                        <p className="text-white text-sm font-medium">Cierre de Tickets</p>
                        <p className="text-slate-400 text-xs">Notificar cuando se cierra un ticket que abrí</p>
                      </div>
                    </div>
                    <Switch
                      checked={editData.notify_on_ticket_close}
                      onCheckedChange={(checked) => setEditData({ ...editData, notify_on_ticket_close: checked })}
                      data-testid="notification-ticket-close-switch"
                    />
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                    <div className="flex items-center gap-3">
                      <UsersRound className="h-5 w-5 text-orange-400" />
                      <div>
                        <p className="text-white text-sm font-medium">Nuevas Asignaciones</p>
                        <p className="text-slate-400 text-xs">Notificar cuando me asignan una tarea</p>
                      </div>
                    </div>
                    <Switch
                      checked={editData.notify_on_new_assignment}
                      onCheckedChange={(checked) => setEditData({ ...editData, notify_on_new_assignment: checked })}
                      data-testid="notification-assignment-switch"
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-4 pt-4 border-t border-[#1E1E5F]">
                <div className="flex items-center justify-between p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <div className="flex items-center gap-3">
                    {editData.is_active ? (
                      <CheckCircle2 className="h-5 w-5 text-[#42E695]" />
                    ) : (
                      <XCircle className="h-5 w-5 text-red-400" />
                    )}
                    <div>
                      <p className="text-white text-sm font-medium">Estado del Usuario</p>
                      <p className="text-slate-400 text-xs">
                        {editData.is_active ? "Usuario activo y puede iniciar sesión" : "Usuario desactivado"}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={editData.is_active}
                    onCheckedChange={(checked) => setEditData({ ...editData, is_active: checked })}
                    data-testid="user-active-switch"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-[#1E1E5F]">
                <Button
                  variant="outline"
                  onClick={() => {
                    setEditModalOpen(false);
                    setSelectedUser(null);
                  }}
                  className="border-[#1E1E5F] text-slate-300"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancelar
                </Button>
                <Button onClick={handleSaveUser} className="bg-[#42E695] text-[#04042C]" data-testid="save-user-btn">
                  <Save className="h-4 w-4 mr-2" />
                  Guardar Cambios
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
